import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '~/App';
import { useAbilityContext } from '~/components/Can';
import { NavItemsBar } from '~/components/nav/NavItemsBar';
import {
  NavSettingsMenuItem,
  NavSettingsPanel,
} from '~/components/ui/NavSettingsPanel';
import { MenuItem, TopBar } from '~/components/ui/TopBar';
import {
  usePlaceTypeConfigQuery,
  useSiteTypeCountsQuery,
  useSiteTypesQuery,
} from '~/generated/graphql';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { buildNavItems } from '~/utils/nav/buildNavItems';
import { ALL } from '~/utils/nav/constants';

// Legacy types kept during refactor; no longer used

type SitesNavProps = {
  /** Callback when the view changes */
  onViewChange?: (view: string) => void;
  /** Initial view value */
  initialView?: string;
  /** Notify parent of the currently displayed nav types (excludes All/Other in parent if desired) */
  onNavItemsChange?: (navItems: string[]) => void;
};

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;
const titleForType = (type: string, t?: TranslateFn) => {
  if (type === ALL) {
    return t
      ? t('allSites', { defaultValue: `All ${t('site_plural')}` })
      : 'All';
  }
  // Do not translate user-defined labels; keep "Other" literal and avoid pluralizing it
  if (type === 'Other') {
    return 'Other';
  }
  return `${type}`;
};

type SiteTypeRenamedDetail = { from: string; to: string };
type SiteNavShowAllChangedDetail = { key: string; value: boolean };

/** Shared navigation logic hook */
const useSitesNavigation = (
  onViewChange?: (view: string) => void,
  initialView = ''
) => {
  const { t } = useTranslation(['translation', 'nav']);
  const ability = useAbilityContext();
  const localStorage = useLocalStorage();
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState(initialView);
  const { tenant } = useContext(AppContext);
  const tenantKey = tenant?.id || 'default';
  // Gate settings via abilities (owner or any account manager)
  const isOwner = ability.can('manage', 'Account');
  const isOnSettingsPage = useMemo(
    () => location.pathname.startsWith('/spaces'),
    [location.pathname]
  );

  // Helper function to update view and notify parent
  const updateView = useCallback(
    (newView: string) => {
      setView(newView);
      onViewChange?.(newView);
    },
    [onViewChange]
  );

  // Unified helper: set view, persist selection, and navigate to /sites if currently in /spaces
  const setViewAndPersist = useCallback(
    (newView: string) => {
      updateView(newView);
      try {
        // Persist selected view under a dedicated key
        localStorage.set('nav.sites.view', newView);
      } catch (error) {
        console.error('Error setting localStorage:', error);
      }
      if (location.pathname.startsWith('/spaces')) {
        navigate('/sites');
      }
    },
    [updateView, localStorage, location.pathname, navigate]
  );

  // Fetch preset types to compute "Other" membership
  const [presetResult] = useSiteTypesQuery({
    variables: { preset: true },
    requestPolicy: 'cache-and-network',
  });
  // Preset types result used only for computing defaults elsewhere; nav uses alphabetical when not customized
  const presetTypes = useMemo(
    () => presetResult.data?.siteTypes ?? [],
    [presetResult.data]
  );
  const presetSet = useMemo(() => new Set<string>(presetTypes), [presetTypes]);

  // Types in use and whether there are untyped sites derived from counts
  const [typeCountsResult] = useSiteTypeCountsQuery({
    requestPolicy: 'cache-and-network',
  });

  // Fetch tenant-wide place type configuration (server persisted)
  const [configResult] = usePlaceTypeConfigQuery({
    requestPolicy: 'cache-and-network',
  });

  // Types currently in use (exclude null/untyped)
  const typesInUse = useMemo<string[]>(() => {
    const counts = typeCountsResult.data?.siteTypeCounts ?? [];
    const list = counts.map((c) => c.type).filter((t): t is string => !!t);
    return Array.from(new Set(list));
  }, [typeCountsResult.data]);

  // Whether there are untyped sites
  const hasUntyped = useMemo(() => {
    const counts = typeCountsResult.data?.siteTypeCounts ?? [];
    return counts.some((c) => !c.type);
  }, [typeCountsResult.data]);

  // Custom nav types override (per tenant) - prefer server config
  const savedNav: string[] = useMemo(() => {
    const server = configResult.data?.placeTypeConfig?.navTypes ?? [];
    if (server.length) return server;
    // fallback to nothing (computed defaults will apply)
    return [];
  }, [configResult.data]);

  // Whether to show the "All" tab at the beginning of sub-navigation
  const [showAllInNav, setShowAllInNav] = useState<boolean>(true);
  // Hydrate from server config once available
  useEffect(() => {
    const serverVal = configResult.data?.placeTypeConfig?.showAllInNav;
    if (typeof serverVal === 'boolean') {
      setShowAllInNav(serverVal);
    }
  }, [configResult.data]);

  // React to storage/custom events for immediate UI updates when ManagePlaceTypes toggles Show All
  useEffect(() => {
    // Normalized key (preferred): new readers/writers should use only this key
    const newKey = `tid:${tenantKey}:siteNavShowAll`;
    // Legacy keys are still read for backward compatibility with older tabs/bundles
    // that may emit storage/events using the old schema until they refresh. Once
    // all clients migrate (e.g., after one or two release cycles), legacy reads
    // can be removed safely to simplify the listener logic.
    const legacyStorageKey = `tid:${
      tenant?.id ?? ''
    }::config.siteNavShowAll.${tenantKey}`;
    const legacyEventKey = `config.siteNavShowAll.${tenantKey}`;

    const handleStorage = (e: StorageEvent) => {
      if (e.key === newKey || e.key === legacyStorageKey) {
        setShowAllInNav(e.newValue === null ? true : e.newValue === 'true');
      }
    };
    const handleCustom = (e: Event) => {
      const detail = (e as CustomEvent<SiteNavShowAllChangedDetail>).detail;
      if (!detail) return;
      if (detail.key === newKey || detail.key === legacyEventKey) {
        setShowAllInNav(!!detail.value);
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(
      'siteNavShowAllChanged',
      handleCustom as EventListener
    );
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(
        'siteNavShowAllChanged',
        handleCustom as EventListener
      );
    };
  }, [tenant?.id, tenantKey]);

  // React to type rename events to keep current view in sync (e.g., Cabin -> Cabine)
  useEffect(() => {
    const onRenamed = (e: Event) => {
      const detail = (e as CustomEvent<SiteTypeRenamedDetail>).detail;
      if (!detail) return;
      if (view === detail.from) {
        setViewAndPersist(detail.to);
      }
    };
    window.addEventListener('siteTypeRenamed', onRenamed as EventListener);
    return () => {
      window.removeEventListener('siteTypeRenamed', onRenamed as EventListener);
    };
  }, [view, setViewAndPersist]);

  // Base list for computing "Other" and nav items (removed unused baseTypes)

  // Build nav items using shared utility (ContactsNav behavior as baseline)
  const navItems = useMemo<string[]>(() => {
    const configuredValues = (configResult.data?.placeTypeConfig?.types ??
      []) as string[];
    const orderMode =
      (configResult.data?.placeTypeConfig?.orderMode as 'ALPHA' | 'MANUAL') ??
      'ALPHA';
    return buildNavItems({
      savedNav,
      configuredValues,
      presets: presetTypes,
      typesInUse,
      hasUntyped,
      showAllInNav,
      orderMode,
      topN: 6,
    });
  }, [
    savedNav,
    showAllInNav,
    typesInUse,
    hasUntyped,
    presetTypes,
    configResult.data?.placeTypeConfig?.types,
    configResult.data?.placeTypeConfig?.orderMode,
  ]);

  // Removed autoTopTypes and forced top-4 path to simplify logic

  // If "All" is removed while currently selected, switch to first available tab
  useEffect(() => {
    if (view === 'All' && (!showAllInNav || !navItems.includes('All'))) {
      const fallback = navItems.find((v) => v !== 'All');
      if (fallback) {
        setViewAndPersist(fallback);
      }
    }
  }, [view, showAllInNav, navItems, setViewAndPersist]);

  // If the current view becomes invalid (e.g., type deleted), jump to first available tab
  useEffect(() => {
    if (!view) return;
    if (!navItems.includes(view) && navItems.length > 0) {
      setViewAndPersist(navItems[0]);
    }
  }, [view, navItems, setViewAndPersist]);

  const hasSpaces = ability.can('manage', 'feat.items');

  const remember = (pathname: string) => {
    if (
      pathname &&
      (pathname.startsWith('/sites') || pathname.startsWith('/spaces'))
    ) {
      // Persist last visited path under a dedicated key
      localStorage.set('nav.sites.lastPath', pathname);
    }
  };

  // Wrapper for event-based calls (legacy compatibility)
  const rememberFromEvent = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const { pathname } = event.currentTarget;
    remember(pathname);
  };

  // Provide FAB actions based on current route
  const fabActions = useMemo(() => {
    if (isOnSettingsPage) {
      return [{ to: '/spaces/new', label: t('space') }];
    }
    return [{ to: 'new', label: t('site') }];
  }, [isOnSettingsPage, t]);

  const fetching =
    presetResult.fetching || typeCountsResult.fetching || configResult.fetching;

  // Initialize view from localStorage or first item
  useEffect(() => {
    if (!fetching && !view && navItems.length > 0) {
      try {
        // Initialize from dedicated view key, with legacy fallback
        const savedViewNew = localStorage.get('nav.sites.view') as
          | string
          | null;
        const savedViewLegacy = localStorage.get('nav.sites') as string | null;
        let initialView = savedViewNew ?? '';

        // Fallback: legacy key may contain either a type or a path; ignore paths
        if (
          !initialView &&
          savedViewLegacy &&
          !savedViewLegacy.startsWith('/')
        ) {
          initialView = savedViewLegacy;
        }

        // Default to first available nav item if empty/invalid
        if (!initialView || !navItems.includes(initialView)) {
          initialView = navItems[0];
        }

        updateView(initialView);
      } catch (error) {
        console.error('Error accessing localStorage:', error);
        updateView(navItems[0]);
      }
    }
  }, [fetching, navItems, view, localStorage, updateView]);

  return {
    t,
    ability,
    localStorage,
    location,
    navigate,
    view,
    setView: updateView,
    setViewAndPersist,
    navItems,
    siteTypes: typesInUse,
    presetSet,
    isCustomized:
      configResult.data?.placeTypeConfig?.isCustomized ?? savedNav.length > 0,
    hasSpaces,
    remember,
    rememberFromEvent,
    fabActions,
    fetching,
    isOnSettingsPage,
    isOwner,
  };
};

/** Create spaces menu item */
const createSpacesMenuItem = (
  t: (key: string) => string,
  hasSpaces: boolean,
  remember: (event: React.MouseEvent<HTMLAnchorElement>) => void,
  isSettingsItem = false
) => {
  /* if (!hasSpaces) return []; */

  const baseItem = {
    key: 'spaces',
    label: t('space_plural'),
    to: '/spaces',
  };

  if (isSettingsItem) {
    return [
      {
        ...baseItem,
        activeKey: 'spaces',
        onClick: remember,
      },
    ];
  }

  return [
    {
      ...baseItem,
      isConfigItem: true,
    },
  ];
};

type SitesNavModel = ReturnType<typeof useSitesNavigation>;

const Mobile = ({ nav }: { nav: SitesNavModel }) => {
  const {
    t,
    location,
    navItems,
    siteTypes,
    hasSpaces,
    remember,
    rememberFromEvent,
    view,
    setViewAndPersist,
    isOnSettingsPage,
    isOwner,
    presetSet,
  } = nav;
  const getCurrentPageName = () => {
    if (location.pathname.startsWith('/spaces')) return t('space_plural');

    // If 0 or 1 actual site types, always show 'Places'
    if (siteTypes.length <= 1) {
      return 'Places';
    }

    // Otherwise, use the current view to determine the title
    if (view) {
      if (view === ALL) return ALL;
      if (view === 'Other') return 'Other';
      return presetSet.has(view) ? `${view}` : view;
    }

    return 'Places';
  };

  // Create menu items for the TopBar
  const menuItems: MenuItem[] = [
    ...navItems.map((type) => ({
      key: `site-type-${type}`,
      label: type === ALL || presetSet.has(type) ? titleForType(type, t) : type,
      isActive: !isOnSettingsPage && type === view,
      onClick: () => setViewAndPersist(type),
    })),
    // Config items (mobile) — owners only
    ...(isOwner
      ? createSpacesMenuItem(t, hasSpaces, rememberFromEvent, false).map(
          (item): MenuItem => ({
            key: item.key,
            label: item.label,
            to: item.to,
            isConfigItem:
              'isConfigItem' in item ? item.isConfigItem : undefined,
          })
        )
      : []),
  ];

  const handleMenuItemClick = useCallback(
    (item: MenuItem) => {
      if (item.to) {
        remember(item.to);
      }
    },
    [remember]
  );
  return (
    <TopBar
      title={
        view === ALL
          ? t('myPlaces', { defaultValue: 'My Places' })
          : getCurrentPageName()
      }
      menuItems={menuItems}
      onMenuItemClick={handleMenuItemClick}
    />
  );
};

const Desktop = ({ nav }: { nav: SitesNavModel }) => {
  const {
    t,
    localStorage,
    navigate,
    view,
    navItems,
    hasSpaces,
    rememberFromEvent,
    setViewAndPersist,
    isOnSettingsPage,
    isOwner,
    fabActions,
  } = nav;
  // Keep key local to this scope since it's only used for redirects here
  const LAST_PATH_KEY = 'nav.sites.lastPath';

  // Overflow handled by NavItemsBar

  // Initialize settings state - only read from localStorage
  const [showSettings, setShowSettings] = useState(() => {
    try {
      const saved = localStorage.get('nav.sites.settings') as string | null;
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Custom setter that saves to localStorage immediately
  const setShowSettingsAndSave = useCallback(
    (newValue: boolean) => {
      setShowSettings(newValue);
      try {
        localStorage.set('nav.sites.settings', newValue.toString());
      } catch {
        // Ignore localStorage errors
      }

      // If closing settings and currently on a settings page, redirect to default
      if (!newValue) {
        if (isOnSettingsPage) {
          // Get the last remembered sites path or default to /sites
          const lastSitesPathNew = localStorage.get(LAST_PATH_KEY) as
            | string
            | null;
          const lastSitesPathLegacy = localStorage.get('nav.sites') as
            | string
            | null;
          const lastSitesPath = lastSitesPathNew ?? lastSitesPathLegacy;
          const defaultPath =
            lastSitesPath && lastSitesPath.startsWith('/sites')
              ? lastSitesPath
              : '/sites';
          navigate(defaultPath);
        }
      }
    },
    [localStorage, navigate, isOnSettingsPage]
  );

  const shouldShowSettings = showSettings;
  /* const hasSettingsItems = hasSpaces; */
  const hasSettingsItems = true;

  // Check if settings are closed but we're on a settings page (e.g., on page load)
  // Only run once on mount to avoid duplicate navigation
  const hasRunRedirectRef = useRef(false);
  useEffect(() => {
    if (hasRunRedirectRef.current) return;
    if (!showSettings && isOnSettingsPage) {
      hasRunRedirectRef.current = true;
      // Get the last remembered sites path or default to /sites
      const lastSitesPathNew = localStorage.get(LAST_PATH_KEY) as string | null;
      const lastSitesPathLegacy = localStorage.get('nav.sites') as
        | string
        | null;
      const lastSitesPath = lastSitesPathNew ?? lastSitesPathLegacy;
      const defaultPath =
        lastSitesPath && lastSitesPath.startsWith('/sites')
          ? lastSitesPath
          : '/sites';
      navigate(defaultPath);
    }
  }, [showSettings, localStorage, navigate, isOnSettingsPage]);

  // Create settings menu items
  const settingsMenuItems: NavSettingsMenuItem[] = [
    // Spaces first
    ...createSpacesMenuItem(t, hasSpaces, rememberFromEvent, true),
    // Settings after spaces: goes straight to Manage Place Types
    {
      key: 'places-settings',
      label: 'Place Types',
      to: '#org-settings-open:placeTypes',
      showPopupIcon: true,
    },
  ];

  return (
    <NavItemsBar
      items={navItems}
      activeItem={!isOnSettingsPage ? view : undefined}
      onSelect={setViewAndPersist}
      labelFor={(v) => (v === ALL ? ALL : titleForType(v, t))}
      recomputeKey={shouldShowSettings}
      isPanelOpen={isOwner ? shouldShowSettings : undefined}
      rightSlot={
        isOwner ? (
          <NavSettingsPanel
            isOpen={shouldShowSettings}
            onToggle={() => setShowSettingsAndSave(!showSettings)}
            menuItems={settingsMenuItems}
            hasItems={hasSettingsItems}
          />
        ) : null
      }
      farRightSlot={
        fabActions.length > 0 ? (
          <Link className='add-button' to={fabActions[0].to}>
            + {fabActions[0].label}
          </Link>
        ) : null
      }
    />
  );
};

export function SitesNav({
  onViewChange,
  initialView,
  onNavItemsChange,
}: SitesNavProps = {}) {
  const nav = useSitesNavigation(onViewChange, initialView);
  const { navItems } = nav;
  const { isMobile } = useBreakpoint();

  useEffect(() => {
    // Emit current nav items to parent when they change
    onNavItemsChange?.(navItems);
  }, [onNavItemsChange, navItems]);

  return isMobile ? <Mobile nav={nav} /> : <Desktop nav={nav} />;
}
